/**
 * PresenceCalendar — grille mensuelle de gestion des présences au jour le jour.
 *
 * Affiche, pour un enfant et un mois, l'état cantine + périscolaire (matin/soir)
 * de chaque jour réservé. Permet de cocher/décocher chaque service si la
 * réservation est modifiable (parent avant cutoff) ou si l'appelant est staff
 * (forceEditable). Mobile-friendly : liste de jours empilés, contrôles tactiles.
 *
 * Réutilise les API existantes getCalendrier + toggleReservation (cantine et
 * périscolaire), sans nouveau backend.
 */

import { useEffect, useMemo, useState } from 'react';
import { api, ApiException } from '@/lib/api';
import { Button, Alert } from '@/components/ui';
import { ChevronLeft, ChevronRight, Utensils, Sun, Moon } from 'lucide-react';
import type {
  CantineReservation, PeriscolaireReservation,
} from '@roronoa-collectivites/shared-types';

function ymd(d: Date): string { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function moisLabel(d: Date): string { return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }); }
function jourLabel(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });
}
function startOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

const RESERVE = (s: string) => s === 'reserve' || s === 'present';

export function PresenceCalendar({ enfantId, forceEditable = false }: { enfantId: string; forceEditable?: boolean }) {
  const [mois, setMois] = useState<Date>(startOfMonth(new Date()));
  const [cantine, setCantine] = useState<CantineReservation[]>([]);
  const [peri, setPeri] = useState<PeriscolaireReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const from = ymd(startOfMonth(mois));
  const to = ymd(endOfMonth(mois));

  async function load() {
    setLoading(true); setError(null);
    try {
      const [c, p] = await Promise.all([
        api.scolaire.getCalendrier(enfantId, from, to).catch(() => ({ reservations: [] as CantineReservation[] })),
        api.scolaire.periscolaire.getCalendrier(enfantId, from, to).catch(() => ({ reservations: [] as PeriscolaireReservation[] })),
      ]);
      setCantine(c.reservations); setPeri(p.reservations);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [enfantId, from, to]);

  // Index par date
  const jours = useMemo(() => {
    const set = new Set<string>();
    cantine.forEach((r) => set.add(r.dateService));
    peri.forEach((r) => set.add(r.dateService));
    return Array.from(set).sort();
  }, [cantine, peri]);

  const cantineDe = (d: string) => cantine.find((r) => r.dateService === d);
  const periDe = (d: string, creneau: 'matin' | 'soir') => peri.find((r) => r.dateService === d && r.creneau === creneau);

  async function toggleCantine(r: CantineReservation) {
    const key = `c-${r.id}`; setBusyKey(key); setError(null);
    try {
      await api.scolaire.toggleReservation({ reservationId: r.id, reserve: !RESERVE(r.statut) });
      await load();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Modification impossible'); }
    finally { setBusyKey(null); }
  }
  async function togglePeri(r: PeriscolaireReservation) {
    const key = `p-${r.id}`; setBusyKey(key); setError(null);
    try {
      await api.scolaire.periscolaire.toggleReservation({ reservationId: r.id, reserve: !RESERVE(r.statut) });
      await load();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Modification impossible'); }
    finally { setBusyKey(null); }
  }

  const canEdit = (modifiable?: boolean) => forceEditable || modifiable === true;

  function Pastille({ on, editable, busy, onClick, icon: Icon, label }: {
    on: boolean; editable: boolean; busy: boolean; onClick: () => void;
    icon: typeof Utensils; label: string;
  }) {
    return (
      <button type="button" disabled={!editable || busy} onClick={onClick} aria-label={label}
        className={[
          'inline-flex h-9 items-center gap-1.5 rounded-r-pill px-3 text-xs font-medium transition-all',
          on ? 'bg-cantine text-white shadow-soft' : 'border border-border text-ink-subtle',
          editable && !busy ? 'hover:opacity-80 cursor-pointer' : 'cursor-not-allowed opacity-60',
        ].join(' ')}>
        <Icon className="h-3.5 w-3.5" /> {label}
      </button>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => setMois(startOfMonth(new Date(mois.getFullYear(), mois.getMonth() - 1, 1)))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium text-ink capitalize">{moisLabel(mois)}</span>
        <Button variant="ghost" size="sm" onClick={() => setMois(startOfMonth(new Date(mois.getFullYear(), mois.getMonth() + 1, 1)))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {loading ? (
        <p className="text-sm text-ink-muted">Chargement…</p>
      ) : jours.length === 0 ? (
        <p className="text-sm text-ink-subtle">Aucune réservation ce mois-ci. Les jours sont créés à l'inscription (cantine / périscolaire).</p>
      ) : (
        <ul className="space-y-1.5">
          {jours.map((d) => {
            const c = cantineDe(d);
            const pm = periDe(d, 'matin');
            const ps = periDe(d, 'soir');
            return (
              <li key={d} className="flex flex-wrap items-center gap-2 rounded-r-md border border-border bg-surface/40 px-3 py-2">
                <span className="w-24 shrink-0 text-sm capitalize text-ink">{jourLabel(d)}</span>
                <div className="flex flex-wrap gap-1.5">
                  {c && <Pastille on={RESERVE(c.statut)} editable={canEdit(c.modifiable)} busy={busyKey === `c-${c.id}`} onClick={() => toggleCantine(c)} icon={Utensils} label="Cantine" />}
                  {pm && <Pastille on={RESERVE(pm.statut)} editable={canEdit(pm.modifiable)} busy={busyKey === `p-${pm.id}`} onClick={() => togglePeri(pm)} icon={Sun} label="Matin" />}
                  {ps && <Pastille on={RESERVE(ps.statut)} editable={canEdit(ps.modifiable)} busy={busyKey === `p-${ps.id}`} onClick={() => togglePeri(ps)} icon={Moon} label="Soir" />}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-2xs text-ink-subtle">
        {forceEditable
          ? 'En tant qu\u2019agent, vous pouvez modifier toutes les présences, même après le délai limite.'
          : 'Cliquez pour décocher une absence. Au-delà du délai limite, contactez la mairie.'}
      </p>
    </div>
  );
}
